// Cold-boot repair of the persisted broiler batch list (app_store key `ppp-v4`).
//
// WHAT THIS ACTUALLY IS — and it is NOT five dated migrations
// -----------------------------------------------------------
// The block this module replaces was described in the lane document as "five
// dated data migrations feeding a persisted repair". Its remaining operations
// fall into two groups that behave completely differently:
//
//   LIVE, RECURRING LIFECYCLE RULES — these are not migrations at all and will
//   never go inert. They re-run on every cold boot, for every batch, forever:
//     * auto-activate: a `planned` batch becomes `active` on its hatch day
//       (hotfix 2026-05-05), and
//     * stamp feed costs: an activating batch missing a starter OR a standard
//       cost gets all three stamped from `ppp-feed-costs-v1`, and
//     * auto-process: an `active` batch becomes `processed` on or after its
//       processing date (hotfix 2026).
//   The two status rules are ORDERED: activation runs first, so a batch whose
//   hatch date AND processing date are both due advances planned -> active ->
//   processed in a single pass. Reordering them strands it at `active`.
//
//   LEGACY ONE-TIME REPAIRS — undated, and every one of them is a no-op against
//   the production blob as of 2026-08-14 (see the `prod-snapshot-2026-08-14`
//   case in the baseline fixture, whose expected `changed` is false):
//     * `archived` is renamed to `processed`,
//     * B-24-02..B-25-01 are forced to the FR breed and the Freedom Ranger
//       hatchery, and
//     * B-25-02 / B-25-03 are forced to their non-standard legacy hatcheries.
//   Brooder entry is no longer repaired: hatch day is a valid planned entry,
//   and a different stored entry may be an explicit historical farm fact.
//
// Being inert in production is NOT a reason to delete them: this runs against
// whatever blob the environment actually holds, and the e2e scenario seeds in
// tests/scenarios/ write partial and legacy-shaped batches on purpose.
//
// THE ONE THAT IS EASY TO MISS
// ----------------------------
// `normalizeFeedCosts` is called UNCONDITIONALLY at the top of the repair, not
// inside any rule. It is what keeps a feed-cost string persisted by an older
// build from being stamped, as a string, onto a batch record that this same
// pass is about to write back to app_store. Dropping it would reintroduce the
// `.toFixed(3)` crash class by the back door. See src/lib/feedCosts.ts.
//
// WHY `changed` MATTERS
// --------------------
// The caller pushes `{key: 'ppp-v4', changed, data}` onto the boot maintenance
// ops. runBootMaintenance (src/app/maintenance/bootMaintenance.ts) upserts ONLY
// when `changed` is set and reports SKIPPED_EQUAL otherwise, so this flag is the
// difference between a farm-record write and no write at all. A rule that
// mutates a batch without setting it would silently lose the repair on reload;
// a rule that sets it without mutating anything would rewrite the whole blob on
// every boot.
//
// EXTRACTED, NOT REWRITTEN
// ------------------------
// This is a behavioural extraction of the 84-line block that used to sit inline
// in loadAllDataBody (src/App.tsx). Equivalence is pinned case-by-case by
// tests/fixtures/broiler-boot-repair-baseline.json, which was generated by
// running the ORIGINAL inline code as a black box over 29 named cases — one of
// them the live production blob.
// G3 deliberately retires only the brooder-entry +1 rule; the fixture records
// that approved expectation change while preserving the other repair pins.
//
// KNOWN SECOND COPY: src/app/data/routeDataCatalog.ts carries
// `normalizeLightHomeBroilerBatch`, a re-implementation of the same rule set for
// the route-local Light Home projection (deliberately without the feed-cost
// stamp and without any write). That duplication is documented there and is
// guarded here by broilerBootRepair.test.ts, which fails if the two copies'
// legacy constants drift apart.

import {shouldAutoActivateBroilerBatch, shouldAutoProcessBroilerBatch} from '../../lib/broiler.js';
import type {BroilerBatch} from '../../lib/broiler.js';
import {normalizeFeedCosts} from '../../lib/feedCosts.js';

/** Batches forced to the Freedom Ranger breed and hatchery, matched upper-cased. */
export const LEGACY_FR_BATCH_NAMES: readonly string[] = Object.freeze(['B-24-02', 'B-24-03', 'B-24-04', 'B-25-01']);

export const LEGACY_FR_BREED = 'FR';
export const LEGACY_FR_HATCHERY = 'Freedom Ranger Hatchery';

/**
 * The two early-2025 batches Ronnie ran from non-standard hatcheries before the
 * dropdown was canonicalized. Order is the original inline order and is kept
 * because it is the order `changed` is accumulated in.
 */
export const LEGACY_HATCHERY_OVERRIDES: readonly (readonly [string, string])[] = Object.freeze([
  Object.freeze(['B-25-02', 'VALLEY FARMS'] as const),
  Object.freeze(['B-25-03', 'CREDO FARMS'] as const),
]);

export type BroilerBootRepairResult = {
  /** The repaired list. A fresh array; unrepaired elements are the SAME objects. */
  batches: BroilerBatch[];
  /** True when any rule mutated any batch — the caller's app_store write gate. */
  changed: boolean;
};

/**
 * Apply the boot repair to the persisted `ppp-v4` list.
 *
 * @param batches         the raw `app_store['ppp-v4']` value, already proved truthy
 *                        by the caller (a non-array truthy value throws here
 *                        exactly as the inline block did).
 * @param storedFeedCosts the raw `app_store['ppp-feed-costs-v1']` value,
 *                        normalized here rather than by the caller so the
 *                        unconditional part of the repair cannot be left behind.
 *
 * Never mutates `batches` or its elements: every rule replaces `nb` with a fresh
 * spread first, so the object the cost stamp writes into is always a copy this
 * function created.
 *
 * Time-dependent by design — the two lifecycle rules compare against today's
 * date through their own helpers, so freeze the clock to test them.
 */
export function applyBroilerBootRepair(batches: BroilerBatch[], storedFeedCosts: unknown): BroilerBootRepairResult {
  let changed = false;
  // LOAD boundary. Two distinct jobs, and it is worth being exact about
  // the second because it is narrower than it looks:
  //   1. the in-memory feed costs become numbers, which is what stops the
  //      `.toFixed(3)` crash on every surface that reads them;
  //   2. the auto-activate path below stamps these values onto a batch
  //      that has none, so normalizing here keeps NEW strings out of
  //      persisted batch records.
  // It does NOT repair batch records that ALREADY carry a string cost —
  // that write is gated on `!nb.perLbStarterCost`, and a non-empty string
  // is truthy, so those rows are skipped. They are repaired by the next
  // successful save, which re-fans normalized values to active records.
  const loadedFeedCosts = normalizeFeedCosts(storedFeedCosts);

  const migrated = batches.map((b: BroilerBatch) => {
    let nb = b;
    if (b.status === 'archived') {
      nb = {...nb, status: 'processed'};
      changed = true;
    }
    // Hotfix 2026-05-05: planned broiler batches become active on
    // hatch day without requiring an edit/save. Persist it so the
    // active-only public weigh-in mirror sees the batch too.
    if (shouldAutoActivateBroilerBatch(nb)) {
      nb = {...nb, status: 'active'};
      // The OR is load-bearing: ONE missing cost restamps ALL THREE, so a
      // batch carrying a starter cost but no standard cost has its starter
      // cost overwritten too. Pinned by the
      // `planned-with-only-one-cost-set-restamps-all-three` baseline case.
      if (!nb.perLbStarterCost || !nb.perLbStandardCost) {
        nb.perLbStarterCost = loadedFeedCosts.starter || 0;
        nb.perLbStandardCost = loadedFeedCosts.grower || 0;
        nb.perLbGritCost = loadedFeedCosts.grit || 0;
      }
      changed = true;
    }
    // Hotfix 2026: active broiler batches auto-advance to processed on
    // or after their processing date (inclusive), without an edit —
    // mirrors hatch-day auto-activation. Runs after activation so a
    // freshly-activated batch sees its current status; only active
    // batches with a due processingDate are affected, so it never
    // fights activation or overrides a manual/terminal status. Persist
    // so the active-only public weigh-in mirror drops the batch too.
    if (shouldAutoProcessBroilerBatch(nb)) {
      nb = {...nb, status: 'processed'};
      changed = true;
    }
    // Hard-code Freedom Ranger breed/hatchery for legacy B-24-02..B-25-01 batches
    const nameUpper = String(nb.name || '').toUpperCase();
    if (LEGACY_FR_BATCH_NAMES.includes(nameUpper)) {
      if (nb.breed !== LEGACY_FR_BREED) {
        nb = {...nb, breed: LEGACY_FR_BREED};
        changed = true;
      }
      if (nb.hatchery !== LEGACY_FR_HATCHERY) {
        nb = {...nb, hatchery: LEGACY_FR_HATCHERY};
        changed = true;
      }
    }
    // Hard-code legacy hatchery names for two early-2025 batches
    // (Ronnie ran these from non-standard hatcheries before the
    // dropdown was canonicalized).
    for (const [legacyName, legacyHatchery] of LEGACY_HATCHERY_OVERRIDES) {
      if (nameUpper === legacyName && nb.hatchery !== legacyHatchery) {
        nb = {...nb, hatchery: legacyHatchery};
        changed = true;
      }
    }
    return nb;
  });

  return {batches: migrated, changed};
}
